import {weakMemo} from '@packages/utilities/memo';
import {
  deriveNativeIdFromPath,
  derivePathFromNativeId,
} from '@packages/utilities/platform';
import {Component} from '@packages/systems/wfdl';
import {
  type NativeId,
  convertElementIdToNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {reportBug} from '@packages/utilities/reporter';

type TransformablePayloadShape = {
  nativeIdInCurrentComponent?: string | null;
  nativeIdPath?: NativeId[];
} & {
  [key: string]: any;
};

/**
 * This utility is shared between the NODE_CLICKED and NODE_HOVERED action creators
 * to ensure their payload is consistently transformed to include both `nativeIdInCurrentComponent` and `nativeIdPath`.
 */
export const mem_transformDesignerPayload = weakMemo(
  <T extends TransformablePayloadShape>(payload: T) =>
    weakMemo((components: Component.ComponentMap) =>
      weakMemo((focusedSymbolNativeIdPath: NativeId[]): T => {
        const {nativeIdPath: payloadNativeIdPath, nativeIdInCurrentComponent} =
          payload;

        let nativeIdPath: NativeId[];
        if (payloadNativeIdPath) {
          nativeIdPath = payloadNativeIdPath;
        } else if (nativeIdInCurrentComponent === null) {
          nativeIdPath = [];
        } else if (nativeIdInCurrentComponent) {
          // It is possible that NODE_HOVERED or NODE_CLICKED are called with a `nativeId` that represents a User Component instance.
          // In this case, we need to make sure we're constructing the `nativeIdPath` correctly, including the instance id
          // and its root as well as considering a dynamic instance suffix can exist
          const maybeNativeIdPath = derivePathFromNativeId(
            convertElementIdToNativeId(nativeIdInCurrentComponent),
            focusedSymbolNativeIdPath,
            components
          );

          if (maybeNativeIdPath) {
            nativeIdPath = maybeNativeIdPath;
          } else {
            return payload;
          }
        } else {
          reportBug(
            'TransfromPayloadMissingNativeIdOrNativeIdPath',
            'Attmpted to transform NODE_HOVERED or NODE_CLICKED payload without `nativeIdPath` or `nativeIdInCurrentComponent` in payload. Make sure at least one of these is provided.',
            {
              team: 'dxp',
              ticket: 'https://webflow.atlassian.net/browse/DESXP-6502',
              payload,
            }
          );
          return payload;
        }

        const newPayload = {
          ...payload,
          nativeIdPath,
          nativeIdInCurrentComponent: deriveNativeIdFromPath(
            nativeIdPath,
            focusedSymbolNativeIdPath
          ),
        } as const;

        return newPayload;
      })
    )
);
